import { useToaster } from '@innovationdepartment/proxima-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logError } from '../lib/logError';
import type { ApiResult, CustomAudiencesApiError } from '../types';

export type CustomAudiencesRequestOptions<T> = {
  /** Success toast, fixed or built from the response, or none when omitted. */
  success?: string | ((data: T) => string);
  /** Error toast prefix, read as "<prefix>: <API message>", or no toast when omitted. */
  error?: string;
};

export type CustomAudiencesRequest = {
  /**
   * Runs a request keyed by `key` and resolves to its result.
   *
   * - Resolves to null when the component unmounted before the request finished; drop the result.
   * - A request that throws settles as an error result with a null status, so the key never stays pending.
   * - Tracks pending state and the last error per key.
   */
  run: <T>(
    key: string,
    request: () => Promise<ApiResult<T>>,
    options?: CustomAudiencesRequestOptions<T>
  ) => Promise<ApiResult<T> | null>;
  isPending: (key: string) => boolean;
  /** The last error for `key`, cleared when the key runs again or by `clearError`. */
  errorFor: (key: string) => CustomAudiencesApiError | null;
  clearError: (key: string) => void;
  /** A toast for outcomes that do not come from an API result, such as the create outcome. */
  toast: (message: string, variant: 'success' | 'error') => void;
};

/**
 * Request execution for the Custom Audiences containers: pending flags, error state, and toasts.
 *
 * Wraps any `CustomAudiencesApi`-backed call that resolves to `ApiResult`, including store actions, so containers never hand-roll try/catch, status flags, or toast calls.
 * Every returned function is stable across renders, so they are safe in effect dependencies.
 */
const useCustomAudiencesRequest = (): CustomAudiencesRequest => {
  const { showToaster } = useToaster();
  /* useToaster returns a new function each render; read the latest through a ref */
  const toasterRef = useRef(showToaster);
  useEffect(() => {
    toasterRef.current = showToaster;
  });
  const mountedRef = useRef(true);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, CustomAudiencesApiError | null>>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(
    async <T>(
      key: string,
      request: () => Promise<ApiResult<T>>,
      options: CustomAudiencesRequestOptions<T> = {}
    ): Promise<ApiResult<T> | null> => {
      setPending(previous => ({ ...previous, [key]: true }));
      setErrors(previous => ({ ...previous, [key]: null }));
      const result = await settle(request);
      if (!mountedRef.current) return null;
      setPending(previous => ({ ...previous, [key]: false }));

      if (result.error) {
        setErrors(previous => ({ ...previous, [key]: result.error }));
        if (options.error)
          toasterRef.current({
            message: `${options.error}: ${result.error.message}`,
            variant: 'error'
          });
        return result;
      }
      const { success } = options;
      if (success)
        toasterRef.current({
          message: typeof success === 'function' ? success(result.data) : success,
          variant: 'success'
        });
      return result;
    },
    []
  );

  const clearError = useCallback(
    (key: string) => setErrors(previous => ({ ...previous, [key]: null })),
    []
  );
  const toast = useCallback(
    (message: string, variant: 'success' | 'error') => toasterRef.current({ message, variant }),
    []
  );

  return useMemo(
    () => ({
      run,
      isPending: (key: string) => Boolean(pending[key]),
      errorFor: (key: string) => errors[key] ?? null,
      clearError,
      toast
    }),
    [run, pending, errors, clearError, toast]
  );
};

/* Every `CustomAudiencesApi` call resolves to `ApiResult`, so a throw is a caller bug or a mock gap; it is reported, not propagated */
const settle = async <T>(request: () => Promise<ApiResult<T>>): Promise<ApiResult<T>> => {
  try {
    return await request();
  } catch (thrown) {
    logError('request.threw', thrown);
    return {
      error: { status: null, message: thrown instanceof Error ? thrown.message : String(thrown) }
    };
  }
};

export default useCustomAudiencesRequest;
